<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Top Indexing</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <!-- Configuration Form -->
      <form v-if="!hasResults" @submit.prevent="runIndexValues">
        <div class="mb-3">
          <label class="form-label">Columns</label>
          <Multiselect
            v-model="selectedColumns"
            :options="availableColumns"
            mode="tags"
            :searchable="true"
            :close-on-select="false"
            placeholder="All columns (leave empty for full analysis)"
            no-options-text="No columns available"
            no-results-text="No columns match your search"
          />
          <small class="text-muted">Leave empty to analyze all columns, or select specific ones</small>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Query Engine</label>
            <select class="form-select" v-model="queryEngine">
              <option value="athena">Athena</option>
              <option value="analytics">Analytics</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Sort Order</label>
            <select class="form-select" v-model="sortOrder">
              <option value="desc">Highest Index First</option>
              <option value="asc">Lowest Index First</option>
            </select>
          </div>
        </div>

        <div class="alert alert-info small">
          <i class="fa fa-info-circle me-2"></i>
          <strong>Index Value</strong> measures how much more (or less) likely an attribute value is to appear in this audience compared to the overall population.
          A value of 200 means twice as likely, 50 means half as likely.
        </div>

        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>
      </form>

      <!-- Results Display -->
      <div v-else>
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Calculating index values...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
          <button type="button" class="btn btn-outline-secondary btn-sm ms-3" @click="resetResults">
            <i class="fa fa-arrow-left me-1"></i>
            Try Again
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">
              <i class="fa fa-arrow-up-right-dots me-2"></i>
              Top Indexing Results
            </h5>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetResults">
              <i class="fa fa-arrow-left me-1"></i>
              Run Another
            </button>
          </div>

          <!-- Tabs for Single Attributes and Combinations -->
          <ul v-if="hasBothTypes" class="nav nav-pills mb-3">
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: viewMode === 'single' }"
                href="#"
                @click.prevent="viewMode = 'single'"
              >
                <i class="fa fa-list me-1"></i>
                Single Attributes
                <span class="badge bg-secondary ms-1">{{ singleColumns.length }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: viewMode === 'combination' }"
                href="#"
                @click.prevent="viewMode = 'combination'"
              >
                <i class="fa fa-table-cells me-1"></i>
                Combinations
                <span class="badge bg-secondary ms-1">{{ combinationColumns.length }}</span>
              </a>
            </li>
          </ul>

          <!-- Column Tabs (for current view mode) -->
          <ul v-if="currentViewColumns.length > 1" class="nav nav-tabs mb-3" style="flex-wrap: wrap;">
            <li v-for="col in currentViewColumns" :key="col" class="nav-item">
              <a
                class="nav-link"
                :class="{ active: activeResultTab === col }"
                href="#"
                @click.prevent="activeResultTab = col"
              >
                {{ formatColumnName(col) }}
              </a>
            </li>
          </ul>

          <!-- Single column header when only one in view -->
          <h6 v-else-if="currentViewColumns.length === 1" class="mb-3">
            <span v-if="isComboColumn(currentViewColumns[0])">
              Columns: <strong>{{ formatColumnName(currentViewColumns[0]) }}</strong>
            </span>
            <span v-else>
              Column: <strong>{{ formatColumnName(currentViewColumns[0]) }}</strong>
            </span>
          </h6>

          <!-- Results for active tab -->
          <div v-if="activeResultData">
            <div v-if="activeResultData.data?.length === 0" class="text-center py-4 text-muted">
              No index data found for this column.
            </div>

            <!-- Single column table -->
            <div v-else-if="!isComboColumn(activeResultTab)" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead class="sticky-top bg-white">
                  <tr>
                    <th>Value</th>
                    <th class="text-end">Index</th>
                    <th style="width: 40%;">Relative Index</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in activeResultData.data" :key="index">
                    <td>{{ item.value || '(empty)' }}</td>
                    <td class="text-end">
                      <span :class="getIndexClass(item.indexValue)">
                        {{ formatIndexValue(item.indexValue) }}
                      </span>
                    </td>
                    <td>
                      <div class="index-bar-container">
                        <div
                          class="index-bar"
                          :class="getIndexBarClass(item.indexValue)"
                          :style="{ width: getIndexBarWidth(item.indexValue) + '%' }"
                        ></div>
                        <div class="index-baseline"></div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Combination column table -->
            <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-hover">
                <thead class="sticky-top bg-white">
                  <tr>
                    <th v-for="colName in getComboColumnNames(activeResultTab)" :key="colName">
                      {{ formatAttributeName(colName) }}
                    </th>
                    <th class="text-end">Index</th>
                    <th style="width: 30%;">Relative Index</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in activeResultData.data" :key="index">
                    <td v-for="(val, vIndex) in splitComboValue(item.value)" :key="vIndex">
                      {{ val || '(empty)' }}
                    </td>
                    <td class="text-end">
                      <span :class="getIndexClass(item.indexValue)">
                        {{ formatIndexValue(item.indexValue) }}
                      </span>
                    </td>
                    <td>
                      <div class="index-bar-container">
                        <div
                          class="index-bar"
                          :class="getIndexBarClass(item.indexValue)"
                          :style="{ width: getIndexBarWidth(item.indexValue) + '%' }"
                        ></div>
                        <div class="index-baseline"></div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="activeResultData.data?.length > 0" class="mt-3 text-muted small">
              Showing {{ activeResultData.data.length }} values
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer">
      <button
        v-if="!hasResults"
        type="button"
        class="btn btn-primary"
        @click="runIndexValues"
        :disabled="loading"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-play me-1"></i>
        Analyze Index
      </button>
      <button
        v-if="hasResults && !loading && !error"
        type="button"
        class="btn btn-primary"
        @click="downloadResults"
      >
        <i class="fa fa-download me-1"></i>
        Download CSV
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        {{ hasResults ? 'Close' : 'Cancel' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Multiselect from '@vueform/multiselect'

export default {
  name: 'TopIndexing',
  components: {
    Multiselect,
  },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedColumns: [],
      queryEngine: 'analytics',
      sortOrder: 'desc',
      indexResults: {},
      hasResults: false,
      activeResultTab: null,
      resultColumns: [],
      viewMode: 'single',
    }
  },
  computed: {
    audience() {
      return this.modalData?.audience
    },
    availableColumns() {
      return this.modalData?.columns || []
    },
    activeResultData() {
      if (!this.activeResultTab) return null
      return this.indexResults[this.activeResultTab]
    },
    criterion() {
      return this.modalData?.criterion || '{}'
    },
    singleColumns() {
      return this.resultColumns.filter(col => !this.isComboColumn(col))
    },
    combinationColumns() {
      return this.resultColumns.filter(col => this.isComboColumn(col))
    },
    hasBothTypes() {
      return this.singleColumns.length > 0 && this.combinationColumns.length > 0
    },
    currentViewColumns() {
      if (!this.hasBothTypes) {
        return this.resultColumns
      }
      return this.viewMode === 'single' ? this.singleColumns : this.combinationColumns
    },
  },
  methods: {
    ...mapActions({
      runIndexValuesQuery: 'audience/runIndexValuesQuery',
      addToast: 'addToast',
    }),
    async runIndexValues() {
      this.loading = true
      this.error = null
      this.hasResults = true

      try {
        const criterion = JSON.parse(this.criterion)

        const queryBody = {
          dataset: this.audience.audsum_dataset,
          criterion: criterion.criterion || criterion,
        }

        if (this.selectedColumns.length > 0) {
          queryBody.attribute_names = this.selectedColumns
        }

        const result = await this.runIndexValuesQuery({
          dataEnvironmentId: this.audience.audsum_data_environment,
          queryBody,
          queryEngine: this.queryEngine,
        })

        this.parseIndexResults(result)
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to run index values query'
      }

      this.loading = false
    },
    parseIndexResults(result) {
      if (result?.data?.body && Array.isArray(result.data.body)) {
        const header = result.data.header || []
        const nameIndex = header.indexOf('attribute_name')
        const valueIndex = header.indexOf('attribute_value')
        const indexValueIndex = header.indexOf('index_value')

        // Group results by column name
        const columnData = {}
        result.data.body.forEach(row => {
          const colName = row[nameIndex !== -1 ? nameIndex : 0]
          const value = row[valueIndex !== -1 ? valueIndex : 1]
          const indexValue = parseFloat(row[indexValueIndex !== -1 ? indexValueIndex : 2]) || 0

          // Skip total count marker
          if (colName === '-' && value === '-') return

          if (!columnData[colName]) {
            columnData[colName] = []
          }
          columnData[colName].push({ value, indexValue })
        })

        // Sort data within each column
        Object.keys(columnData).forEach(col => {
          columnData[col].sort((a, b) => {
            return this.sortOrder === 'desc'
              ? b.indexValue - a.indexValue
              : a.indexValue - b.indexValue
          })
        })

        this.resultColumns = Object.keys(columnData)

        // Update results for each column
        this.resultColumns.forEach(col => {
          this.indexResults = {
            ...this.indexResults,
            [col]: { data: columnData[col] }
          }
        })

        // Set initial view mode and active tab
        const singleCols = this.resultColumns.filter(c => !c.includes('{)(}'))
        const comboCols = this.resultColumns.filter(c => c.includes('{)(}'))

        if (singleCols.length > 0) {
          this.viewMode = 'single'
          this.activeResultTab = singleCols[0]
        } else if (comboCols.length > 0) {
          this.viewMode = 'combination'
          this.activeResultTab = comboCols[0]
        } else {
          this.activeResultTab = this.resultColumns[0] || null
        }
      } else {
        this.resultColumns = []
      }
    },
    resetResults() {
      this.hasResults = false
      this.indexResults = {}
      this.activeResultTab = null
      this.resultColumns = []
      this.viewMode = 'single'
      this.error = null
    },
    // Helper methods for combo columns
    isComboColumn(colName) {
      return colName && colName.includes('{)(}')
    },
    formatColumnName(colName) {
      if (this.isComboColumn(colName)) {
        return colName.split('{)(}').map(c => this.formatAttributeName(c)).join(' + ')
      }
      return this.formatAttributeName(colName)
    },
    formatAttributeName(name) {
      if (!name) return ''
      return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    getComboColumnNames(colName) {
      if (!colName) return []
      return colName.split('{)(}')
    },
    splitComboValue(value) {
      if (!value) return ['']
      return value.split('{)(}')
    },
    formatIndexValue(value) {
      if (value == null) return '-'
      return Math.round(value)
    },
    getIndexClass(value) {
      if (value == null) return ''
      if (value >= 150) return 'text-success fw-bold'
      if (value >= 100) return 'text-success'
      if (value >= 75) return 'text-warning'
      return 'text-danger'
    },
    getIndexBarClass(value) {
      if (value == null) return ''
      if (value >= 100) return 'index-bar-high'
      return 'index-bar-low'
    },
    getIndexBarWidth(value) {
      if (value == null) return 0
      // Scale: 0 = 0%, 100 = 50%, 200+ = 100%
      const normalized = Math.min(value / 200, 1) * 100
      return Math.max(normalized, 2) // Minimum 2% for visibility
    },
    downloadResults() {
      const headers = ['Column', 'Value', 'Index Value']
      const rows = []

      this.resultColumns.forEach(col => {
        const resultData = this.indexResults[col]
        if (resultData?.data?.length) {
          resultData.data.forEach(item => {
            rows.push([
              `"${this.formatColumnName(col)}"`,
              `"${(item.value || '').toString().replace(/"/g, '""')}"`,
              item.indexValue,
            ])
          })
        }
      })

      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `top_indexing_${this.audience.audience_name}_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(link.href)

      this.addToast({ type: 'success', message: 'CSV downloaded successfully' })
    },
  },
  watch: {
    viewMode(newMode) {
      const cols = newMode === 'single' ? this.singleColumns : this.combinationColumns
      if (cols.length > 0 && !cols.includes(this.activeResultTab)) {
        this.activeResultTab = cols[0]
      }
    },
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
}

.nav-pills {
  gap: 0.5rem;
}

.nav-pills .nav-link {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.nav-pills .nav-link:not(.active) {
  background-color: #f8f9fa;
  color: #495057;
}

.nav-pills .nav-link:not(.active):hover {
  background-color: #e9ecef;
}

/* Index bar visualization */
.index-bar-container {
  position: relative;
  height: 20px;
  background: #f8f9fa;
  border-radius: 4px;
  overflow: hidden;
}

.index-bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.index-bar-high {
  background: linear-gradient(90deg, #d4edda 0%, #28a745 100%);
}

.index-bar-low {
  background: linear-gradient(90deg, #f8d7da 0%, #dc3545 100%);
}

.index-baseline {
  position: absolute;
  left: 50%;
  top: 0;
  width: 2px;
  height: 100%;
  background: #6c757d;
  opacity: 0.5;
}
</style>
